import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Plus, Repeat, Search, Trash2 } from 'lucide-react'
import { Page } from '@/components/Page'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { DataTable } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { LoadingSpinner } from '@/components/LoadingSpinner'
import { Modal } from '@/components/Modal'
import { FormField } from '@/components/FormField'
import { useToast } from '@/hooks/useToast'
import {
  useRecurringInvoiceTemplates,
  useCreateRecurringInvoiceTemplate,
  useDeleteRecurringInvoiceTemplate,
  useUpdateRecurringInvoiceTemplate,
  useInvoices,
} from '@/features/invoicing/queries'
import type {
  RecurringInvoiceCadence,
  RecurringInvoiceTemplate,
  RecurringInvoiceTemplatePayload,
} from '@/features/invoicing/types'
import { formatDateTime } from '@/lib/locale'

/**
 * Phase 2b management surface for recurring invoices. Wraps the
 * generate_recurring_invoice workflow step type with a dedicated UI:
 *   - Pick a posted invoice as the template
 *   - Choose cadence (weekly/monthly/quarterly/yearly/custom-cron)
 *   - Activate / deactivate; the hourly scheduler clones on schedule
 */

const CADENCES: RecurringInvoiceCadence[] = ['weekly', 'monthly', 'quarterly', 'yearly', 'custom']

export function RecurringInvoicesPage() {
  const { toast } = useToast()
  const { data, isLoading } = useRecurringInvoiceTemplates()
  // Source-invoice picker pulls posted invoices only (the service
  // rejects drafts on create, so showing them here would just confuse).
  const invQ = useInvoices()
  const postedInvoices = useMemo(
    () => (invQ.data?.invoices ?? []).filter(i => !!i.posted_at),
    [invQ.data],
  )
  const createMut = useCreateRecurringInvoiceTemplate()
  const updateMut = useUpdateRecurringInvoiceTemplate()
  const deleteMut = useDeleteRecurringInvoiceTemplate()

  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState<RecurringInvoiceTemplatePayload>({
    name: '',
    source_invoice_ids: [],
    cadence: 'monthly',
    due_in_days: 30,
    is_active: true,
  })
  // Search + selection state for the multi-select source picker. Held
  // outside the payload so the search box stays responsive without
  // re-rendering the whole form on every keystroke.
  const [sourceSearch, setSourceSearch] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<RecurringInvoiceTemplate | null>(null)

  function reset() {
    setDraft({ name: '', source_invoice_ids: [], cadence: 'monthly', due_in_days: 30, is_active: true })
    setSourceSearch('')
  }

  // Reset the search box when the modal opens/closes so a stale query
  // doesn't pre-filter the next time the user opens it.
  useEffect(() => { if (!open) setSourceSearch('') }, [open])

  async function onCreate() {
    if (!draft.name.trim()) { toast({ title: 'Name is required', variant: 'destructive' }); return }
    if (draft.source_invoice_ids.length === 0) {
      toast({ title: 'Pick at least one source invoice', variant: 'destructive' }); return
    }
    if (draft.cadence === 'custom' && !(draft.cron_expression || '').trim()) {
      toast({ title: 'Custom cadence needs a cron expression', variant: 'destructive' }); return
    }
    try {
      await createMut.mutateAsync(draft)
      toast({ title: 'Recurring template created' })
      setOpen(false)
      reset()
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed', variant: 'destructive' })
    }
  }

  // Posted invoices filtered by the search box. Matches against invoice
  // number and customer name so the user can find by either; the cap
  // (~200 hits) keeps the DOM cheap on tenants with thousands of invoices.
  const filteredSources = useMemo(() => {
    const q = sourceSearch.trim().toLowerCase()
    const list = postedInvoices
    if (!q) return list.slice(0, 200)
    return list.filter(i =>
      (i.invoice_number ?? '').toLowerCase().includes(q) ||
      (i.customer_display_name ?? '').toLowerCase().includes(q),
    ).slice(0, 200)
  }, [postedInvoices, sourceSearch])

  const allFilteredSelected = filteredSources.length > 0 &&
    filteredSources.every(i => draft.source_invoice_ids.includes(i.id))

  function toggleSource(id: number) {
    setDraft(d => {
      const has = d.source_invoice_ids.includes(id)
      return {
        ...d,
        source_invoice_ids: has
          ? d.source_invoice_ids.filter(x => x !== id)
          : [...d.source_invoice_ids, id],
      }
    })
  }

  function selectAllFiltered() {
    setDraft(d => {
      const next = new Set(d.source_invoice_ids)
      for (const i of filteredSources) next.add(i.id)
      return { ...d, source_invoice_ids: Array.from(next) }
    })
  }

  function clearAllFiltered() {
    setDraft(d => {
      const filteredIds = new Set(filteredSources.map(i => i.id))
      return {
        ...d,
        source_invoice_ids: d.source_invoice_ids.filter(id => !filteredIds.has(id)),
      }
    })
  }

  async function toggleActive(t: RecurringInvoiceTemplate) {
    try {
      await updateMut.mutateAsync({ id: t.id, body: { is_active: !t.is_active } })
      toast({ title: t.is_active ? 'Paused' : 'Resumed' })
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed', variant: 'destructive' })
    }
  }

  async function onDelete() {
    if (!deleteTarget) return
    try {
      await deleteMut.mutateAsync(deleteTarget.id)
      toast({ title: 'Deleted' })
      setDeleteTarget(null)
    } catch (err) {
      toast({ title: err instanceof Error ? err.message : 'Failed', variant: 'destructive' })
    }
  }

  const columns = useMemo(() => [
    { accessorKey: 'name', header: 'Name',
      cell: ({ row }: { row: { original: RecurringInvoiceTemplate } }) => {
        const sources = row.original.source_invoices ?? []
        const first = sources[0]
        const extra = sources.length - 1
        return (
          <div>
            <div className="font-medium">{row.original.name}</div>
            {first ? (
              <div className="text-xs text-muted-foreground">
                From <Link to={`/invoicing/invoices/${first.id}`} className="text-primary hover:underline">{first.invoice_number}</Link>
                {first.customer_display_name ? <> · {first.customer_display_name}</> : null}
                {extra > 0 ? <> + {extra} more</> : null}
              </div>
            ) : row.original.source_invoice_number ? (
              <div className="text-xs text-muted-foreground">
                From <Link to={`/invoicing/invoices/${row.original.source_invoice_id}`} className="text-primary hover:underline">{row.original.source_invoice_number}</Link>
                {row.original.customer_display_name ? <> · {row.original.customer_display_name}</> : null}
              </div>
            ) : null}
          </div>
        )
      },
    },
    { accessorKey: 'cadence', header: 'Cadence' },
    { accessorKey: 'next_run_at', header: 'Next run',
      cell: ({ row }: { row: { original: RecurringInvoiceTemplate } }) => row.original.next_run_at ? formatDateTime(row.original.next_run_at) : <span className="text-muted-foreground">-</span>,
    },
    { accessorKey: 'last_run', header: 'Last run',
      cell: ({ row }: { row: { original: RecurringInvoiceTemplate } }) => {
        if (!row.original.last_run_at) return <span className="text-muted-foreground">never</span>
        const status = row.original.last_run_status
        return (
          <div>
            <div>{formatDateTime(row.original.last_run_at)}</div>
            {status === 'failed' ? (
              <div className="text-xs text-destructive">{row.original.last_run_error || 'failed'}</div>
            ) : status === 'partial' ? (
              <>
                <Badge variant="warning" className="text-[0.65rem]">partial</Badge>
                {row.original.last_run_error ? (
                  <div className="text-xs text-warning-foreground">{row.original.last_run_error}</div>
                ) : null}
              </>
            ) : (
              <Badge variant="success" className="text-[0.65rem]">ok</Badge>
            )}
          </div>
        )
      },
    },
    { accessorKey: 'is_active', header: 'Status',
      cell: ({ row }: { row: { original: RecurringInvoiceTemplate } }) =>
        row.original.is_active ? <Badge variant="success">active</Badge> : <Badge variant="secondary">paused</Badge>,
    },
    { accessorKey: 'actions', header: '',
      cell: ({ row }: { row: { original: RecurringInvoiceTemplate } }) => (
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => toggleActive(row.original)}>
            {row.original.is_active ? 'Pause' : 'Resume'}
          </Button>
          <Button size="icon" variant="ghost" className="size-7" onClick={() => setDeleteTarget(row.original)} aria-label="Delete">
            <Trash2 className="size-4 text-destructive" />
          </Button>
        </div>
      ),
    },
  ], [])

  return (
    <Page
      title="Recurring invoices"
      description="Template invoices that auto-clone on a schedule. Each run creates a new draft from the source - you still review and send."
      actions={<Button onClick={() => setOpen(true)}><Plus className="mr-1 size-4" /> New recurring</Button>}
    >
      {isLoading ? (
        <Card><div className="flex justify-center py-16"><LoadingSpinner /></div></Card>
      ) : (data?.templates ?? []).length === 0 ? (
        <Card>
          <EmptyState
            icon={Repeat}
            title="No recurring invoices yet"
            description="Pick any posted invoice and set a cadence. The scheduler creates a fresh copy on each tick."
            action={<Button onClick={() => setOpen(true)}><Plus className="mr-1 size-4" /> New recurring</Button>}
          />
        </Card>
      ) : (
        <Card><div className="p-3"><DataTable columns={columns as never} rows={data?.templates ?? []} /></div></Card>
      )}

      <Modal open={open} onOpenChange={(o) => { if (!o) { reset(); } setOpen(o) }} title="New recurring invoice template">
        <div className="space-y-3">
          <FormField label="Name" required value={draft.name} onChange={e => setDraft({ ...draft, name: e.currentTarget.value })} hint="e.g. Acme Co - monthly retainers" />

          <div>
            <div className="mb-1.5 flex items-center justify-between">
              <label className="text-sm font-medium">Source invoices <span className="text-destructive">*</span></label>
              <span className="text-xs text-muted-foreground">
                {draft.source_invoice_ids.length} selected
              </span>
            </div>
            <div className="rounded-md border bg-card">
              <div className="flex flex-wrap items-center gap-2 border-b p-2">
                <div className="relative flex-1 min-w-[160px]">
                  <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" aria-hidden />
                  <Input
                    value={sourceSearch}
                    onChange={e => setSourceSearch(e.currentTarget.value)}
                    placeholder="Search by invoice # or customer…"
                    className="h-8 pl-7 text-sm"
                  />
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant={allFilteredSelected ? 'secondary' : 'outline'}
                  onClick={() => allFilteredSelected ? clearAllFiltered() : selectAllFiltered()}
                  disabled={filteredSources.length === 0}
                >
                  {allFilteredSelected ? 'Clear all' : 'Select all'}
                </Button>
              </div>
              <div className="max-h-64 overflow-auto">
                {postedInvoices.length === 0 ? (
                  <div className="p-4 text-center text-xs text-muted-foreground">
                    No posted invoices yet. Post an invoice first - drafts can't seed recurring runs.
                  </div>
                ) : filteredSources.length === 0 ? (
                  <div className="p-4 text-center text-xs text-muted-foreground">
                    No invoices match "{sourceSearch}".
                  </div>
                ) : (
                  <ul className="divide-y">
                    {filteredSources.map(i => {
                      const checked = draft.source_invoice_ids.includes(i.id)
                      return (
                        <li key={i.id}>
                          <label className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-accent">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={() => toggleSource(i.id)}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate font-medium">
                                {i.invoice_number}
                                {i.customer_display_name ? <span className="text-muted-foreground"> · {i.customer_display_name}</span> : null}
                              </span>
                              <span className="block text-xs text-muted-foreground">
                                {i.total} {i.currency}
                              </span>
                            </span>
                          </label>
                        </li>
                      )
                    })}
                  </ul>
                )}
              </div>
            </div>
            {postedInvoices.length > filteredSources.length && !sourceSearch ? (
              <p className="mt-1 text-xs text-muted-foreground">
                Showing {filteredSources.length} of {postedInvoices.length}. Type to search.
              </p>
            ) : null}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <FormField
              label="Cadence"
              control={
                <select
                  className="h-10 w-full rounded-md border bg-card px-3 text-sm"
                  value={draft.cadence}
                  onChange={e => setDraft({ ...draft, cadence: e.currentTarget.value as RecurringInvoiceCadence })}
                >
                  {CADENCES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              }
            />
            <FormField
              label="Due in (days)"
              type="number"
              min={0}
              value={String(draft.due_in_days ?? 30)}
              onChange={e => setDraft({ ...draft, due_in_days: parseInt(e.currentTarget.value, 10) || 0 })}
            />
          </div>
          {draft.cadence === 'custom' ? (
            <FormField
              label="Cron expression"
              value={draft.cron_expression || ''}
              onChange={e => setDraft({ ...draft, cron_expression: e.currentTarget.value })}
              hint="5-field cron (UTC), e.g. 0 9 1 * * for the 1st at 09:00"
            />
          ) : null}
          <FormField
            label="Start date (optional)"
            type="datetime-local"
            value={draft.start_at || ''}
            onChange={e => setDraft({ ...draft, start_at: e.currentTarget.value })}
            hint="When the first clone fires. Defaults to one cadence period from now."
          />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={onCreate} disabled={createMut.isPending}>Create</Button>
          </div>
        </div>
      </Modal>

      <Modal.Confirm
        open={deleteTarget !== null}
        onOpenChange={(o) => { if (!o) setDeleteTarget(null) }}
        title={deleteTarget ? `Delete "${deleteTarget.name}"?` : ''}
        body="Removes the template entirely. Already-cloned invoices stay where they are."
        confirmLabel="Delete"
        confirmVariant="danger"
        busy={deleteMut.isPending}
        onConfirm={onDelete}
      />
    </Page>
  )
}
